import React, { useRef } from 'react'
import {
  motion,
  useScroll,
  useVelocity,
  useSpring,
  useTransform,
  useMotionValue,
  useAnimationFrame,
  useReducedMotion,
} from 'framer-motion'

const wrap = (min, max, value) => {
  const range = max - min
  return ((((value - min) % range) + range) % range) + min
}

/**
 * Marquee that drifts on its own and reacts to the page scroll: scrolling
 * down speeds it up, scrolling up drags it backwards. Falls back to a static
 * row when motion is reduced.
 */
const VelocityMarquee = ({ items, baseSpeed = 3, className = 'marquee', trackClassName = 'marquee-track' }) => {
  const reduced = useReducedMotion()
  const baseX = useMotionValue(0)
  const direction = useRef(1)

  const { scrollY } = useScroll()
  const scrollVelocity = useVelocity(scrollY)
  const smoothVelocity = useSpring(scrollVelocity, { damping: 50, stiffness: 400 })
  const velocityFactor = useTransform(smoothVelocity, [0, 1200], [0, 4], { clamp: false })

  // Two copies of the row sit side by side, so wrapping at -50% is seamless.
  const x = useTransform(baseX, (v) => `${wrap(-50, 0, v)}%`)

  useAnimationFrame((_, delta) => {
    if (reduced) return
    let move = direction.current * baseSpeed * (delta / 1000)

    const factor = velocityFactor.get()
    if (factor < 0) direction.current = -1
    else if (factor > 0) direction.current = 1

    move += move * Math.abs(factor)
    baseX.set(baseX.get() + move)
  })

  const row = [...items, ...items]

  return (
    <div className={className}>
      <motion.div className={trackClassName} style={reduced ? undefined : { x }}>
        {row.map((item, i) => (
          <span key={`${item}-${i}`} className='marquee-item'>
            {item} <span className='marquee-dot'>✦</span>
          </span>
        ))}
      </motion.div>
    </div>
  )
}

export default VelocityMarquee
